<script setup lang="ts">
import type { CartItem } from '~/types'

const props = defineProps<{
  item: CartItem
}>()

const emit = defineEmits<{
  updateQuantity: [productId: string, quantity: number]
  remove: [productId: string]
}>()

const handleIncrement = () => {
  emit('updateQuantity', props.item.productId, props.item.quantity + 1)
}

const handleDecrement = () => {
  if (props.item.quantity > 1) {
    emit('updateQuantity', props.item.productId, props.item.quantity - 1)
  }
}

const handleRemove = () => {
  emit('remove', props.item.productId)
}

// Format pack size display
const packSizeDisplay = computed(() => {
  if (!props.item.packSize) return null
  return `${props.item.packSize} ${props.item.unit}`
})
</script>

<template>
  <div class="group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 rounded-xl p-4 md:p-5 -mx-4 border-2 border-transparent hover:border-primary-100 dark:hover:border-primary-900/30">
    <div class="flex items-start gap-4 md:gap-5">
      <!-- Product Image - Enhanced visual with better empty state -->
      <NuxtLink :to="`/catalogo/${item.productSlug}`" class="shrink-0 group/image">
        <div class="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-xl overflow-hidden p-3 md:p-4 shadow-md border-2 border-gray-200 dark:border-gray-700 group-hover/image:shadow-lg group-hover/image:border-primary-200 dark:group-hover/image:border-primary-800 transition-all duration-200 image-container">
          <NuxtImg
            v-if="item.imageUrl"
            :src="item.imageUrl"
            :alt="item.productName"
            class="w-full h-full object-contain group-hover/image:scale-105 transition-transform duration-200"
          />
          <div v-else class="w-full h-full flex items-center justify-center">
            <div class="text-center">
              <UIcon name="i-heroicons-photo" class="w-12 h-12 md:w-14 md:h-14 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
              <p class="text-xs text-gray-400 dark:text-gray-600 font-medium">Sin imagen</p>
            </div>
          </div>
        </div>
      </NuxtLink>

      <!-- Product Info - Better spacing and hierarchy -->
      <div class="flex-1 min-w-0">
        <div class="mb-4">
          <NuxtLink
            :to="`/catalogo/${item.productSlug}`"
            class="font-bold text-base md:text-lg text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-2 leading-tight mb-3 block high-contrast"
          >
            {{ item.productName }}
          </NuxtLink>
          <div class="flex flex-wrap items-center gap-2">
            <span class="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 text-xs font-bold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              <UIcon name="i-heroicons-hashtag" class="w-3.5 h-3.5 mr-1.5" />
              {{ item.productSku }}
            </span>
            <span v-if="packSizeDisplay" class="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 text-xs font-bold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              <UIcon name="i-heroicons-cube" class="w-3.5 h-3.5 mr-1.5" />
              {{ packSizeDisplay }}
            </span>
          </div>
        </div>

        <!-- Mobile: Quantity and Remove in single row -->
        <div class="flex items-center justify-between gap-3 md:hidden mt-5">
          <!-- Quantity Controls - Large touch targets with enhanced styling -->
          <div class="flex items-center gap-1 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 rounded-xl p-1.5 shadow-sm border-2 border-gray-200 dark:border-gray-700">
            <UButton
              color="neutral"
              variant="ghost"
              size="lg"
              icon="i-heroicons-minus-solid"
              :disabled="item.quantity <= 1"
              @click="handleDecrement"
              class="w-12 h-12 interactive-scale disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Disminuir cantidad"
            />
            <div class="w-16 text-center px-2">
              <span class="text-xl font-bold text-gray-900 dark:text-white quantity-display">{{ item.quantity }}</span>
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">uds</p>
            </div>
            <UButton
              color="neutral"
              variant="ghost"
              size="lg"
              icon="i-heroicons-plus-solid"
              @click="handleIncrement"
              class="w-12 h-12 interactive-scale"
              aria-label="Aumentar cantidad"
            />
          </div>

          <!-- Remove Button - Large touch target with danger styling -->
          <UButton
            color="error"
            variant="soft"
            size="lg"
            icon="i-heroicons-trash-solid"
            @click="handleRemove"
            class="w-12 h-12 interactive-scale shadow-sm hover:shadow-md"
            aria-label="Eliminar producto"
          />
        </div>
      </div>

      <!-- Desktop: Quantity and Remove side by side -->
      <div class="hidden md:flex items-center gap-4">
        <!-- Quantity Controls - Large touch targets with enhanced styling -->
        <div class="flex items-center gap-1 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 rounded-xl p-1.5 shadow-sm border-2 border-gray-200 dark:border-gray-700">
          <UButton
            color="neutral"
            variant="ghost"
            size="lg"
            icon="i-heroicons-minus-solid"
            :disabled="item.quantity <= 1"
            @click="handleDecrement"
            class="w-12 h-12 interactive-scale disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Disminuir cantidad"
          />
          <div class="w-20 text-center px-3">
            <span class="text-2xl font-bold text-gray-900 dark:text-white quantity-display">{{ item.quantity }}</span>
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">uds</p>
          </div>
          <UButton
            color="neutral"
            variant="ghost"
            size="lg"
            icon="i-heroicons-plus-solid"
            @click="handleIncrement"
            class="w-12 h-12 interactive-scale"
            aria-label="Aumentar cantidad"
          />
        </div>

        <!-- Remove Button - Large touch target with danger styling -->
        <UButton
          color="error"
          variant="soft"
          size="lg"
          icon="i-heroicons-trash-solid"
          @click="handleRemove"
          class="w-12 h-12 interactive-scale shadow-sm hover:shadow-md"
          aria-label="Eliminar producto"
        />
      </div>
    </div>
  </div>
</template>
